
import React, { useState, useEffect, useRef } from 'react';
import { X, Share2, Rocket, Heart, Flame, Skull, Loader2, Download, Copy, Twitter, Facebook, Instagram, Radiation, Zap, Brain, Microscope, Ghost } from 'lucide-react';
import { UIImage, AuthView } from '../types.ts';
import { supabase } from '../lib/supabase.ts';
import { trackEvent } from '../lib/analytics.ts';

// Custom Brand Icons
const TikTokIcon = ({ size = 24 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.06-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.59-1.01V14.5c0 2.1-.6 4.15-2.02 5.61-1.45 1.5-3.6 2.39-5.71 2.39-2.11 0-4.26-.89-5.71-2.39C1.79 18.65 1.2 16.6 1.2 14.5c0-2.1.59-4.15 2.01-5.61C4.66 7.39 6.81 6.5 8.92 6.5c.34 0 .68.02 1.02.05v4.02c-.34-.03-.68-.05-1.02-.05-1.08 0-2.16.45-2.88 1.19-.72.74-1.02 1.79-1.02 2.79 0 1 .3 2.05 1.02 2.79.72.74 1.8 1.19 2.88 1.19 1.08 0 2.16-.45 2.88-1.19.72-.74 1.02-1.79 1.02-2.79V.02h-.32z" />
  </svg>
);

const SnapchatIcon = ({ size = 24 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.014 1.134c-4.437 0-6.816 3.193-6.816 5.564 0 1.24.496 2.228 1.218 2.228.188 0 .425-.067.59-.164.21-.122.316-.145.474-.145.316 0 .548.243.548.56 0 .213-.09.387-.34.615-.496.449-1.556 1.34-1.556 2.646 0 .618.357 1.1.921 1.487-.225.105-.398.242-.398.535 0 .43.435.632 1.042.632.225 0 .45-.022.69-.074.226.43.743 1.133 2.012 1.133.15 0 .33-.008.533-.024.16.347.534.82 1.396.82.863 0 1.237-.473 1.397-.82.203.016.382.024.532.024 1.27 0 1.787-.703 2.012-1.133.24.052.465.074.69.074.608 0 1.043-.203 1.043-.632 0-.293-.173-.43-.398-.535.564-.387.921-.869.921-1.487 0-1.306-1.06-2.197-1.556-2.646-.25-.228-.339-.402-.339-.615 0-.317.232-.56.548-.56.158 0 .263.023.474.145.165.097.402.164.59.164.722 0 1.218-.988 1.218-2.228 0-2.371-2.379-5.564-6.816-5.564" />
  </svg>
);

const XLitIcon = ({ size = 24 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932 6.064-6.932zm-1.294 19.497h2.039L6.486 3.24H4.298l13.31 17.41z"/>
  </svg>
);

const LIT_SOUND_URL = 'https://assets.mixkit.co/active_storage/sfx/866/866-preview.mp3';

interface MemePreviewModalProps {
  previewImage: UIImage | null;
  setPreviewImage: React.Dispatch<React.SetStateAction<UIImage | null>>;
  user: any;
  isGuest: boolean;
  addAura: (amount: number) => Promise<number>;
  notify: (msg: string, type?: any) => void;
  setAuthView: React.Dispatch<React.SetStateAction<AuthView>>;
}

export default function MemePreviewModal({ 
  previewImage, setPreviewImage, user, isGuest, addAura, notify, setAuthView 
}: MemePreviewModalProps) {
  const [hasVoted, setHasVoted] = useState<boolean | null>(null);
  const [isVoting, setIsVoting] = useState(false);
  const [vibeFeedback, setVibeFeedback] = useState<{ id: string; text: string; type: 'lit' | 'mid' | 'aura' }[]>([]);
  const [isAudioUnlocked, setIsAudioUnlocked] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isPreparingShare, setIsPreparingShare] = useState(false);

  const audioCtxRef = useRef<AudioContext | null>(null);
  const litSoundRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (previewImage) {
      const AudioContextClass = (window.AudioContext || (window as any).webkitAudioContext);
      audioCtxRef.current = new AudioContextClass();
      const audio = new Audio();
      audio.src = LIT_SOUND_URL;
      audio.crossOrigin = "anonymous";
      audio.volume = 0.5;
      litSoundRef.current = audio;
    }
    return () => {
      if (audioCtxRef.current) audioCtxRef.current.close();
      if (litSoundRef.current) {
        litSoundRef.current.pause();
        litSoundRef.current = null;
      }
    };
  }, [previewImage]);

  const unlockAudio = async () => {
    if (isAudioUnlocked) return;
    if (audioCtxRef.current?.state === 'suspended') await audioCtxRef.current.resume();
    litSoundRef.current?.play().then(() => {
      litSoundRef.current!.pause();
      litSoundRef.current!.currentTime = 0;
    }).catch(() => {});
    setIsAudioUnlocked(true);
  };

  useEffect(() => {
    if (previewImage && user && !isGuest) checkVoteStatus();
    else setHasVoted(null);
  }, [previewImage, user, isGuest]);

  const checkVoteStatus = async () => {
    if (!previewImage || !user) return;
    try {
      const { data } = await supabase.from('votes').select('*').eq('user_id', user.id).eq('meme_id', previewImage.id).maybeSingle();
      setHasVoted(!!data);
    } catch (e) { console.error(e); }
  };

  const playFeedbackSound = async (type: 'lit' | 'mid') => {
    if (type === 'lit') {
      litSoundRef.current?.play().catch(() => {});
    } else {
      const ctx = audioCtxRef.current;
      if (!ctx) return;
      if (ctx.state === 'suspended') await ctx.resume();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const now = ctx.currentTime;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(150, now);
      osc.frequency.exponentialRampToValueAtTime(40, now + 0.2);
      gain.gain.setValueAtTime(0.3, now);
      gain.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
      osc.start(now);
      osc.stop(now + 0.3);
    }
  };

  const triggerFeedback = (text: string, type: 'lit' | 'mid' | 'aura') => {
    const id = Math.random().toString(36).substring(7);
    setVibeFeedback(prev => [...prev, { id, text, type }]);
    setTimeout(() => {
      setVibeFeedback(prev => prev.filter(f => f.id !== id));
    }, 1000);
  };

  const handleVote = async (type: 'like' | 'dislike') => {
    unlockAudio();
    if (isGuest) { setAuthView('signup'); return; }
    if (!previewImage || isVoting) return;
    setIsVoting(true);
    playFeedbackSound(type === 'like' ? 'lit' : 'mid');
    try {
      const { error } = await supabase.from('votes').insert({ user_id: user.id, meme_id: previewImage.id, vote_type: type });
      if (error) throw error;
      triggerFeedback(type === 'like' ? 'LIT!' : 'MID...', type === 'like' ? 'lit' : 'mid');
      const gained = await addAura(1);
      triggerFeedback(`+${gained} AURA`, 'aura');
      setHasVoted(true);

      trackEvent('vibe_check_vote', {
        vote_type: type,
        meme_id: previewImage.id,
        rarity: previewImage.rarity,
        source: 'preview_modal'
      });

      notify("VIBE RECORDED", "success");
    } catch (e: any) {
      if (e.code === '23505') { notify("ALREADY VOTED", "info"); setHasVoted(true); }
      else notify("VOTE FAILED", "error");
    } finally { setIsVoting(false); }
  };

  const handleDownload = async (silent = false) => {
    if (!previewImage) return;
    try {
      const response = await fetch(previewImage.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${previewImage.name.replace(/\s+/g, '_')}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      if (!silent) notify("DNA DOWNLOADED", "success");
      return blob;
    } catch (e) {
      console.error(e);
      if (!silent) notify("DOWNLOAD FAILED", "error");
    }
  };

  const handleViralShare = async (platform: 'x' | 'facebook' | 'instagram' | 'tiktok' | 'snapchat' | 'native') => {
    if (!previewImage) return;
    setIsPreparingShare(true);
    
    // We share a link to the image to launch apps correctly and save user data.
    const shareText = `Just cooked absolute peak brainrot: "${previewImage.name}"! 🚀 Cook yours on Brainrot Blast!`;
    const shareUrl = previewImage.url;
    const encodedText = encodeURIComponent(shareText);
    const encodedUrl = encodeURIComponent(shareUrl);

    let finalUrl = '';
    switch (platform) {
      case 'x':
        finalUrl = `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`;
        break;
      case 'facebook':
        finalUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case 'snapchat':
        finalUrl = `https://www.snapchat.com/share?url=${encodedUrl}&text=${encodedText}`;
        break;
      case 'instagram':
      case 'tiktok':
        // These apps do not support direct link-sharing to feed from web browsers.
        // We download the file so the user can upload it manually in the app.
        notify("DOWNLOADING FOR APP UPLOAD...", "info");
        await handleDownload(true);
        notify("IMAGE SAVED! OPEN APP TO POST.", "success");
        setIsPreparingShare(false);
        return;
      case 'native':
        if (navigator.share) {
          try {
            await navigator.share({ title: 'Brainrot Blast!', text: shareText, url: shareUrl });
            notify("SENT TO SYSTEM", "success");
            setIsPreparingShare(false);
            return;
          } catch (e) { console.warn(e); }
        }
        break;
    }

    if (finalUrl) {
      window.open(finalUrl, '_blank', 'noopener,noreferrer');
      notify(`LAUNCHING ${platform.toUpperCase()}...`, "info");
    }

    trackEvent('viral_share', {
      platform: platform,
      meme_id: previewImage.id,
      rarity: previewImage.rarity
    });

    setIsPreparingShare(false);
  };

  if (!previewImage) return null;

  const handleToggleFavorite = async () => {
    if (!previewImage.user_id) return;
    const nextState = !previewImage.is_favorite;
    setPreviewImage({ ...previewImage, is_favorite: nextState });
    try { await supabase.from('memes').update({ is_favorite: nextState }).eq('id', previewImage.id); } 
    catch (e) { console.error(e); }
  };

  const getRarityStyles = (rarity?: string) => {
    switch (rarity) {
      case 'uncommon': return 'border-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.3)]';
      case 'rare': return 'border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.4)]';
      case 'epic': return 'border-purple-500 shadow-[0_0_15px_rgba(168,85,247,0.4)]';
      case 'legendary': return 'border-yellow-400 shadow-[0_0_20px_rgba(250,196,21,0.5)]';
      case 'mythic': return 'border-red-500 animate-pulse shadow-[0_0_25px_rgba(239,68,68,0.5)]';
      case 'god': return 'animate-rainbow-border border-white shadow-[0_0_35px_rgba(255,255,255,0.8)]';
      default: return 'border-zinc-800';
    }
  };

  const isOwnMeme = user && previewImage.user_id === user.id;

  return (
    <div className="fixed inset-0 z-[500] bg-black/95 backdrop-blur-3xl flex items-center justify-center p-4 overflow-y-auto animate-in fade-in" onClick={() => { setPreviewImage(null); setIsShareModalOpen(false); }}>
      <div className="max-w-2xl w-full flex flex-col gap-0 my-auto animate-in zoom-in-95 relative" onClick={e => e.stopPropagation()}>
        
        {/* IMAGE AREA */}
        <div className={`w-full rounded-t-[3.5rem] overflow-hidden border-t-8 border-x-8 relative group bg-zinc-950 ${getRarityStyles(previewImage.rarity)}`}>
          <img src={previewImage.url} className="w-full aspect-square object-contain" alt={previewImage.name} />
          <div className="absolute inset-0 pointer-events-none flex flex-col items-center justify-center gap-4 z-30">
            {vibeFeedback.map(f => (
              <div key={f.id} className={`font-bungee text-5xl uppercase animate-out fade-out slide-out-to-top-20 duration-1000 drop-shadow-[0_0_15px_rgba(0,0,0,0.8)] ${f.type === 'lit' ? 'text-orange-500' : f.type === 'mid' ? 'text-zinc-500' : 'text-yellow-400'}`}>
                {f.text}
              </div>
            ))}
          </div>
          <div className="absolute top-6 right-6 flex items-center gap-2">
            {isOwnMeme && (
              <button onClick={handleToggleFavorite} className={`p-3 rounded-full transition-all backdrop-blur-md border border-white/10 z-10 ${previewImage.is_favorite ? 'bg-pink-600 text-white shadow-[0_0_20px_#db2777]' : 'bg-black/60 text-white hover:bg-black'}`}>
                <Heart size={24} fill={previewImage.is_favorite ? "currentColor" : "none"} />
              </button>
            )}
            <button onClick={() => setPreviewImage(null)} className="p-3 bg-black/60 rounded-full text-white hover:bg-black transition-all backdrop-blur-md border border-white/10 z-10">
              <X size={24}/>
            </button>
          </div>
        </div>

        {/* INFO AREA */}
        <div className={`w-full bg-zinc-950 border-x-8 p-8 space-y-6 shadow-2xl relative ${getRarityStyles(previewImage.rarity)} ${isOwnMeme || hasVoted !== false ? 'rounded-b-[3.5rem] border-b-8' : ''}`}>
          <div className="flex justify-between items-start">
            <div className="space-y-1">
              <h3 className="text-4xl font-bungee text-yellow-400 uppercase drop-shadow-lg leading-none">{previewImage.name}</h3>
              <div className="flex items-center gap-3">
                <span className="text-[10px] font-black text-indigo-400 uppercase tracking-widest bg-indigo-500/10 px-2 py-0.5 rounded border border-indigo-500/20">{previewImage.rarity}</span>
                <span className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">SCORE {previewImage.score || '??'}</span>
              </div>
            </div>
          </div>
          <p className="text-base font-bold italic text-zinc-200 bg-zinc-900/50 p-6 rounded-[2rem] border-2 border-white/5 leading-relaxed relative overflow-hidden">
            <span className="absolute top-0 left-0 w-1 h-full bg-yellow-400"></span>
            "{previewImage.story}"
          </p>

          {/* FORBIDDEN FREQUENCIES (ATTRIBUTES) */}
          {previewImage.attributes && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 pt-2">
              <div className="bg-zinc-900/80 border border-zinc-800 p-3 rounded-2xl flex flex-col items-center gap-1.5 group hover:border-yellow-400/30 transition-all">
                <Radiation size={14} className="text-yellow-400 group-hover:scale-110 transition-transform" />
                <span className="text-[7px] font-black text-zinc-500 uppercase tracking-tighter text-center">COGNITIVE</span>
                <span className="text-[9px] font-black text-white uppercase text-center leading-none">{previewImage.attributes.cognitive_damage}</span>
              </div>
              <div className="bg-zinc-900/80 border border-zinc-800 p-3 rounded-2xl flex flex-col items-center gap-1.5 group hover:border-blue-400/30 transition-all">
                <Zap size={14} className="text-blue-400 group-hover:scale-110 transition-transform" />
                <span className="text-[7px] font-black text-zinc-500 uppercase tracking-tighter text-center">SIGNAL</span>
                <span className="text-[9px] font-black text-white uppercase text-center leading-none">{previewImage.attributes.signal_purity}</span>
              </div>
              <div className="bg-zinc-900/80 border border-zinc-800 p-3 rounded-2xl flex flex-col items-center gap-1.5 group hover:border-purple-400/30 transition-all">
                <Brain size={14} className="text-purple-400 group-hover:scale-110 transition-transform" />
                <span className="text-[7px] font-black text-zinc-500 uppercase tracking-tighter text-center">FORM</span>
                <span className="text-[9px] font-black text-white uppercase text-center leading-none">{previewImage.attributes.thoughtform}</span>
              </div>
              <div className="bg-zinc-900/80 border border-zinc-800 p-3 rounded-2xl flex flex-col items-center gap-1.5 group hover:border-emerald-400/30 transition-all">
                <Microscope size={14} className="text-emerald-400 group-hover:scale-110 transition-transform" />
                <span className="text-[7px] font-black text-zinc-500 uppercase tracking-tighter text-center">ORIGIN</span>
                <span className="text-[9px] font-black text-white uppercase text-center leading-none">{previewImage.attributes.origin_event}</span>
              </div>
              <div className="bg-zinc-900/80 border border-zinc-800 p-3 rounded-2xl flex flex-col items-center gap-1.5 group hover:border-indigo-400/30 transition-all">
                <Ghost size={14} className="text-indigo-400 group-hover:scale-110 transition-transform" />
                <span className="text-[7px] font-black text-zinc-500 uppercase tracking-tighter text-center">SIG</span>
                <span className="text-[9px] font-black text-white uppercase text-center leading-none">{previewImage.attributes.signature}</span>
              </div>
            </div>
          )}

          <div className="flex gap-4 pt-2">
            <button onClick={() => setIsShareModalOpen(true)} className="flex-1 flex items-center justify-center gap-3 px-6 py-6 bg-indigo-600 rounded-3xl border-4 border-indigo-500 font-bungee text-xl uppercase hover:bg-indigo-500 transition-all active:scale-95 shadow-[0_10px_20px_rgba(79,70,229,0.3)]">
              <Share2 size={24}/> Viral Share
            </button>
            {(isOwnMeme || isGuest) && (
              <button onClick={() => handleDownload()} className="flex items-center justify-center p-6 bg-zinc-900 rounded-3xl border-2 border-zinc-800 text-zinc-400 hover:text-white transition-all active:scale-95 shadow-lg">
                <Download size={24}/>
              </button>
            )}
          </div>
        </div>

        {/* VIRAL SHARE OVERLAY */}
        {isShareModalOpen && (
          <div className="absolute inset-0 z-[100] bg-black/95 backdrop-blur-2xl rounded-[3.5rem] p-8 flex flex-col items-center justify-center space-y-8 animate-in zoom-in-95 duration-200 border-8 border-indigo-500/50 shadow-[0_0_100px_rgba(79,70,229,0.3)]">
            <button onClick={() => setIsShareModalOpen(false)} className="absolute top-8 right-8 text-zinc-600 hover:text-white transition-colors"><X size={32}/></button>
            <div className="text-center space-y-2">
              <div className="mx-auto w-20 h-20 bg-indigo-600 rounded-3xl flex items-center justify-center text-white shadow-[0_0_30px_rgba(79,70,229,0.5)] mb-4">
                {isPreparingShare ? <Loader2 className="animate-spin" size={40} /> : <Rocket size={40} className="animate-bounce" />}
              </div>
              <h3 className="text-4xl font-bungee text-white uppercase tracking-tighter">VIRAL BROADCAST</h3>
              <p className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.3em]">PC: WEB LINK | MOBILE: LAUNCH APP</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 w-full">
              <button onClick={() => handleViralShare('x')} className="p-4 bg-black border-2 border-zinc-800 rounded-[2rem] flex flex-col items-center gap-2 hover:border-white transition-all group">
                <div className="w-12 h-12 bg-white text-black rounded-2xl flex items-center justify-center transition-all group-hover:scale-110"><XLitIcon size={24} /></div>
                <span className="font-bungee text-[8px] uppercase text-white">X / Twitter</span>
              </button>
              <button onClick={() => handleViralShare('facebook')} className="p-4 bg-[#1877F2]/10 border-2 border-[#1877F2]/30 rounded-[2rem] flex flex-col items-center gap-2 hover:border-[#1877F2] transition-all group">
                <div className="w-12 h-12 bg-[#1877F2] text-white rounded-2xl flex items-center justify-center transition-all group-hover:scale-110"><Facebook size={24} /></div>
                <span className="font-bungee text-[8px] uppercase text-[#1877F2]">Facebook</span>
              </button>
              <button onClick={() => handleViralShare('instagram')} className="p-4 bg-gradient-to-tr from-[#f09433]/10 via-[#dc2743]/10 to-[#bc1888]/10 border-2 border-[#dc2743]/30 rounded-[2rem] flex flex-col items-center gap-2 hover:border-[#dc2743] transition-all group">
                <div className="w-12 h-12 bg-gradient-to-tr from-[#f09433] via-[#dc2743] to-[#bc1888] text-white rounded-2xl flex items-center justify-center transition-all group-hover:scale-110"><Instagram size={24} /></div>
                <span className="font-bungee text-[8px] uppercase text-[#dc2743]">Instagram</span>
              </button>
              <button onClick={() => handleViralShare('tiktok')} className="p-4 bg-black border-2 border-zinc-800 rounded-[2rem] flex flex-col items-center gap-2 hover:border-[#ff0050] transition-all group">
                <div className="w-12 h-12 bg-white text-black rounded-2xl flex items-center justify-center transition-all group-hover:scale-110"><TikTokIcon size={24} /></div>
                <span className="font-bungee text-[8px] uppercase text-white">TikTok</span>
              </button>
              <button onClick={() => handleViralShare('snapchat')} className="p-4 bg-[#FFFC00]/10 border-2 border-[#FFFC00]/30 rounded-[2rem] flex flex-col items-center gap-2 hover:border-[#FFFC00] transition-all group">
                <div className="w-12 h-12 bg-[#FFFC00] text-black rounded-2xl flex items-center justify-center transition-all group-hover:scale-110"><SnapchatIcon size={24} /></div>
                <span className="font-bungee text-[8px] uppercase text-[#FFFC00]">Snapchat</span>
              </button>
              <button onClick={() => handleViralShare('native')} className="p-4 bg-indigo-600/10 border-2 border-indigo-500/30 rounded-[2rem] flex flex-col items-center gap-2 hover:border-indigo-400 transition-all group">
                <div className="w-12 h-12 bg-indigo-600 text-white rounded-2xl flex items-center justify-center transition-all group-hover:scale-110"><Share2 size={24} /></div>
                <span className="font-bungee text-[8px] uppercase text-indigo-400">Other / Mobile</span>
              </button>
            </div>
            <div className="text-center space-y-2 max-w-sm">
              <p className="text-[10px] font-black text-zinc-500 uppercase tracking-widest leading-relaxed">
                <span className="text-indigo-400">PRO SIGMA TIP:</span> INSTAGRAM AND TIKTOK WILL DOWNLOAD THE IMAGE SO YOU CAN UPLOAD IT MANUALLY IN THE APP.
              </p>
              <button onClick={() => { navigator.clipboard.writeText(previewImage.url); notify("DNA LINK COPIED", "success"); }} className="flex items-center gap-2 mx-auto text-[8px] font-bold text-zinc-600 hover:text-white transition-colors uppercase tracking-widest">
                <Copy size={12}/> Or Copy Direct DNA Link
              </button>
            </div>
          </div>
        )}

        {/* VOTING FOOTER */}
        {!isOwnMeme && hasVoted === false && (
          <div className={`w-full bg-zinc-950 border-x-8 border-b-8 p-8 rounded-b-[3.5rem] flex flex-col gap-6 shadow-2xl animate-in slide-in-from-top-4 ${getRarityStyles(previewImage.rarity)}`}>
            <div className="grid grid-cols-2 gap-6 h-24">
              <button onClick={() => handleVote('like')} disabled={isVoting} className="bg-red-600 border-b-8 border-red-900 rounded-3xl flex items-center justify-center gap-3 font-bungee text-2xl text-white hover:bg-red-500 active:translate-y-2 active:border-b-0 transition-all shadow-[0_0_30px_rgba(220,38,38,0.3)] disabled:opacity-50">
                {isVoting ? <Loader2 className="animate-spin" /> : <><Flame size={32} /> LIT</>}
              </button>
              <button onClick={() => handleVote('dislike')} disabled={isVoting} className="bg-zinc-100 border-b-8 border-zinc-300 rounded-3xl flex items-center justify-center gap-3 font-bungee text-2xl text-zinc-950 hover:bg-white active:translate-y-2 active:border-b-0 transition-all shadow-lg disabled:opacity-50">
                {isVoting ? <Loader2 className="animate-spin" /> : <><Skull size={32} /> MID</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
